import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { debounceTime, distinctUntilChanged, filter, pairwise } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { InputGeneralSelectComponent } from '../../../shared/inputs/input-general-select/input-general-select.component';
import { InputGeneral } from '../../../shared/inputs/input-general/input-general';
import { WorkFlowLocation } from '../../../shared/models/hybrid-model/gps.model';
import { FieldItem } from '../../../shared/models/hybrid-model/filed-input-model';
import { HybridPageService } from '../../../shared/services/hybrid-page.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-hybrid-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputGeneral,
    InputGeneralSelectComponent,
    TranslateModule,
  ],
  templateUrl: './hybrid-form.component.html',
})
export class HybridFormComponent {
  @Input() quotationId = 0;
  @Input() workFlowLocation: WorkFlowLocation | null = null;
  @Input() fieldItem: FieldItem[] | null = null;
  @Input() title: string | null = null;
  @Input() hybridForm: FormGroup<Record<string, FormControl>> = new FormGroup<
    Record<string, FormControl>
  >({});
  @Output() emitHybridForm = new EventEmitter<FormGroup<Record<string, FormControl>>>();

  constructor(private hybridPageService: HybridPageService) {}

  ngOnInit(): void {
    if (this.fieldItem) {
      this.buildDynamicControls(this.fieldItem);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldItem'] && !changes['fieldItem'].firstChange) {
      const newValue = changes['fieldItem'].currentValue;
      this.buildDynamicControls(newValue);
    }
  }

  private itemChangedEvent(FormControl: FormControl, field: FieldItem) {
    if (FormControl) {
      FormControl.valueChanges
        .pipe(debounceTime(700), distinctUntilChanged())
        .subscribe((value) => {
          if (value) {
            this.eventParentField(field, value);
          }
        });
    }
  }

  private buildDynamicControls(items: FieldItem[]) {
    if (!items) return;
    for (const field of items) {
      const key = `${field.editClosingFieldId}`;
      if (!this.hybridForm.contains(key)) {
        const newControl = new FormControl<any>(
          null,
          field.isMandatory ? [Validators.required] : [],
        );
        if (field.selectedValue) {
          if (field.componentTypeId == 10 || field.componentTypeId == 17) {
            newControl.setValue(Number(field.selectedValue) ?? null);
          } else {
            newControl.setValue(field.selectedValue ?? null);
          }
        }
        this.hybridForm.addControl(key, newControl);
        this.itemChangedEvent(newControl, field);
      }
    }
  }

  public OutputHybridForm() {
    this.emitHybridForm.emit(this.hybridForm);
  }

  public eventParentField(field: FieldItem, value: any) {
    this.fieldItem?.forEach((f) => {
      if (f.parentClosingFieldId === field.editClosingFieldId) {
        this.hybridPageService
          .editClosingFieldCatalogList({
            closingFieldId: f.editClosingFieldId,
            componentCatalogId: f.componentCatalogId,
            postalCode: this.workFlowLocation?.postalCode ?? '',
            quotationId: this.quotationId,
            selectedValue: value,
            workflowId: this.workFlowLocation!.workflowId,
            workflowTypeId: this.workFlowLocation!.workflowTypeId,
          })
          .subscribe((response) => {
            if (!response.hasErrors) {
              f.catalog = response.data;
            }
          });
      }
    });
  }

  public regexStringToArray(regexText: string): string[] {
    return regexText
      .replace(/^\(|\)$/g, '')
      .split('|')
      .map((s) => s.trim());
  }
}
